import React from "react";
import PropTypes from "prop-types";
import PageShell from "../common/PageShell";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import LatestMatchCard from "./LatestMatchCard";
import { getMapMetaById } from "../../constants/mapMeta";

const TAB_LIVE = 0;
const TAB_FINISHED = 1;
const TAB_ALL = 2;

function getStatusFilterByTab(tab) {
  if (tab === TAB_LIVE) return "live";
  if (tab === TAB_FINISHED) return "finished";
  return "all";
}

function normalizeIdentifier(value) {
  if (value === null || value === undefined) return null;
  const stringValue = String(value).trim();
  return stringValue ? stringValue : null;
}

function buildIdentitySet(values) {
  const set = new Set();
  values.forEach((value) => {
    const normalized = normalizeIdentifier(value);
    if (normalized) set.add(normalized);
  });
  return set;
}

function matchIncludesIdentity(match, identitySet) {
  if (identitySet.size === 0) return false;
  const roster = [...(match.team1 || []), ...(match.team2 || [])];
  return roster.some(
    (player) =>
      identitySet.has(normalizeIdentifier(player._id)) ||
      identitySet.has(normalizeIdentifier(player.userId)) ||
      identitySet.has(normalizeIdentifier(player.steamId))
  );
}

export default function LatestMatchesPage({ catalog, actions }) {
  const user = catalog?.user;
  const friends = catalog?.friends || [];
  const feed = catalog?.latestMatches || {};
  const matches = feed.matches || [];

  const [tab, setTab] = React.useState(TAB_ALL);
  const [mineOnly, setMineOnly] = React.useState(false);
  const [friendsOnly, setFriendsOnly] = React.useState(false);
  const [selectedMap, setSelectedMap] = React.useState("all");

  const statusFilter = getStatusFilterByTab(tab);

  React.useEffect(() => {
    actions.getLatestMatchesWithPolling({ statusFilter, limit: 40 });
    return () => {
      actions.stopLatestMatchesPolling();
    };
  }, [statusFilter]);

  const myIdentitySet = buildIdentitySet([
    user?._id,
    user?.userId,
    user?.steamId,
    user?.steamId64,
    user?.steam64Id,
  ]);
  const friendIdentitySet = buildIdentitySet(friends.map((friend) => friend.friendSteamId));

  const availableMaps = [];
  const seenMapIds = new Set();
  matches.forEach((match) => {
    if (match.map && !seenMapIds.has(match.map)) {
      seenMapIds.add(match.map);
      availableMaps.push({
        id: match.map,
        display: getMapMetaById(match.map)?.display || match.map,
      });
    }
  });

  const filteredMatches = matches.filter((match) => {
    if (mineOnly && !matchIncludesIdentity(match, myIdentitySet)) return false;
    if (friendsOnly && !matchIncludesIdentity(match, friendIdentitySet)) return false;
    if (selectedMap !== "all" && match.map !== selectedMap) return false;
    return true;
  });

  const isInitialLoading = feed.status === "loading" && matches.length === 0;
  const hasError = feed.status === "error";
  const liveCount = matches.filter((match) => match.status === "live").length;
  const hasActiveFilters = mineOnly || friendsOnly || selectedMap !== "all";

  return (
    <PageShell maxWidth="md">
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
              Latest Matches
            </Typography>
            <Typography sx={{ color: "#999999", mt: 0.25, fontSize: 13 }}>
              See what&apos;s live right now and how recent games wrapped up. Scores update automatically.
            </Typography>
          </Box>

          {liveCount > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#ff5566",
                  boxShadow: "0 0 0 4px rgba(255,85,102,0.18)",
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#ff5566" }}>
                {liveCount} live now
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Tabs
            value={tab}
            onChange={(event, nextTab) => setTab(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Live now" />
            <Tab label="Recently finished" />
            <Tab label="All" />
          </Tabs>
        </Box>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Chip
            label="My matches"
            clickable
            color={mineOnly ? "primary" : "default"}
            variant={mineOnly ? "filled" : "outlined"}
            onClick={() => setMineOnly((value) => !value)}
          />
          <Chip
            label="Friends"
            clickable
            color={friendsOnly ? "primary" : "default"}
            variant={friendsOnly ? "filled" : "outlined"}
            onClick={() => setFriendsOnly((value) => !value)}
          />

          <FormControl size="small" sx={{ minWidth: 160, ml: { sm: 0.5 } }}>
            <InputLabel id="latest-matches-map-filter-label">Map</InputLabel>
            <Select
              labelId="latest-matches-map-filter-label"
              label="Map"
              value={selectedMap}
              onChange={(event) => setSelectedMap(event.target.value)}
            >
              <MenuItem value="all">All maps</MenuItem>
              {availableMaps.map((mapOption) => (
                <MenuItem key={mapOption.id} value={mapOption.id}>
                  {mapOption.display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          {!isInitialLoading && !hasError && (
            <Typography sx={{ fontSize: 12.5, color: "#888888" }}>
              {filteredMatches.length}
              {hasActiveFilters ? ` of ${matches.length}` : ""}{" "}
              {filteredMatches.length === 1 ? "match" : "matches"}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mt: 2, borderColor: "rgba(255,255,255,0.07)" }} />

        <Box sx={{ mt: 2 }}>
          {isInitialLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4, justifyContent: "center" }}>
              <CircularProgress size={22} />
              <Typography sx={{ color: "#999999" }}>Loading matches...</Typography>
            </Box>
          )}

          {hasError && (
            <Typography sx={{ color: "#E8A33D", fontWeight: 600, py: 2 }}>
              {feed.error}
            </Typography>
          )}

          {!isInitialLoading && !hasError && filteredMatches.length === 0 && (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography sx={{ color: "#cccccc", fontWeight: 700 }}>
                {matches.length === 0 ? "No matches yet" : "Nothing matches your filters"}
              </Typography>
              <Typography sx={{ color: "#888888", fontSize: 13, mt: 0.5 }}>
                {matches.length === 0
                  ? "Live and recently finished games will show up here."
                  : "Try clearing a filter to see more games."}
              </Typography>
            </Box>
          )}

          {filteredMatches.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredMatches.map((match) => (
                <LatestMatchCard
                  key={match.matchId}
                  match={match}
                  serverTimeMs={feed.serverTimeMs}
                  isMine={matchIncludesIdentity(match, myIdentitySet)}
                  hasFriends={matchIncludesIdentity(match, friendIdentitySet)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Paper>
    </PageShell>
  );
}

LatestMatchesPage.propTypes = {
  catalog: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};
